import React from 'react';
import {
    Youtube,
    Instagram,
    Twitter,
    Linkedin,
    Facebook,
    FileText,
    Clock,
    CheckCircle,
    AlertCircle,
    XCircle,
    Edit,
    Trash2,
    Play,
    BarChart3,
    Upload,
} from 'lucide-react';
import { ScheduledPost, PlatformConnection, Campaign } from '../../../services/publishing.service';

interface PostCardProps {
    post: ScheduledPost;
    onEdit: (post: ScheduledPost) => void;
    onCancel: (post: ScheduledPost) => void;
    onPublishNow: (post: ScheduledPost) => void;
    onViewAnalytics: (post: ScheduledPost) => void;
    onUploadMedia?: (post: ScheduledPost) => void;
}

const PostCard: React.FC<PostCardProps> = ({
    post,
    onEdit,
    onCancel,
    onPublishNow,
    onViewAnalytics,
    onUploadMedia,
}) => {
    const getPlatformIcon = (platform: string) => {
        const icons: Record<string, React.ReactNode> = {
            youtube: <Youtube className="w-4 h-4" />,
            instagram: <Instagram className="w-4 h-4" />,
            twitter: <Twitter className="w-4 h-4" />,
            linkedin: <Linkedin className="w-4 h-4" />,
            facebook: <Facebook className="w-4 h-4" />,
            tiktok: <div className="w-4 h-4 bg-black dark:bg-white rounded-sm flex items-center justify-center text-white dark:text-black text-xs font-bold">T</div>,
            blog: <FileText className="w-4 h-4" />,
        };
        return icons[platform] || <FileText className="w-4 h-4" />;
    };

    const getPlatformColor = (platform: string) => {
        const colors: Record<string, string> = {
            youtube: 'bg-red-500',
            instagram: 'bg-gradient-to-br from-pink-500 to-purple-600',
            twitter: 'bg-blue-400',
            linkedin: 'bg-blue-600',
            facebook: 'bg-blue-700',
            tiktok: 'bg-black dark:bg-white',
            blog: 'bg-green-500',
        };
        return colors[platform] || 'bg-gray-500';
    };

    const getStatusInfo = (status: string) => {
        const config: Record<string, { style: string; icon: React.ReactNode; label: string }> = {
            draft: {
                style: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
                icon: <Edit className="w-3 h-3" />,
                label: 'Draft',
            },
            scheduled: {
                style: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                icon: <Clock className="w-3 h-3" />,
                label: 'Scheduled',
            },
            ready: {
                style: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                icon: <CheckCircle className="w-3 h-3" />,
                label: 'Ready',
            },
            needs_media: {
                style: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
                icon: <AlertCircle className="w-3 h-3" />,
                label: 'Needs Media',
            },
            publishing: {
                style: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
                icon: <Play className="w-3 h-3" />,
                label: 'Publishing',
            },
            published: {
                style: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
                icon: <CheckCircle className="w-3 h-3" />,
                label: 'Published',
            },
            failed: {
                style: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                icon: <XCircle className="w-3 h-3" />,
                label: 'Failed',
            },
            cancelled: {
                style: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                icon: <XCircle className="w-3 h-3" />,
                label: 'Cancelled',
            },
        };
        return config[status] || config.draft;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const formatNumber = (num: number) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toString();
    };

    const statusInfo = getStatusInfo(post.status);
    const platformConnection = post.platformConnectionId as PlatformConnection;
    const campaign = post.campaignId as Campaign | undefined;
    const platform = typeof platformConnection === 'string' ? 'unknown' : platformConnection?.platform || 'unknown';

    return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-5 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${getPlatformColor(platform)} rounded-xl flex items-center justify-center text-white`}>
                        {getPlatformIcon(platform)}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">{post.title}</h3>
                        {typeof platformConnection !== 'string' && platformConnection?.accountName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">@{platformConnection.accountName}</p>
                        )}
                    </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.style}`}>
                    {statusInfo.icon}
                    {statusInfo.label}
                </span>
            </div>

            {/* Description */}
            {post.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{post.description}</p>
            )}

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-3 mb-4 text-sm">
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(post.scheduledFor)}</span>
                </div>
                {campaign && typeof campaign !== 'string' && (
                    <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg text-xs font-medium">
                        {campaign.name}
                    </span>
                )}
            </div>

            {/* Performance (if published) */}
            {post.status === 'published' && post.performance && (
                <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <div className="text-center">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {formatNumber(post.performance.views)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Views</p>
                    </div>
                    <div className="text-center">
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">
                            {post.performance.engagementRate.toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Engagement</p>
                    </div>
                    <div className="text-center">
                        <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                            {formatNumber(post.performance.clicks)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Clicks</p>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {post.status === 'failed' && post.errorMessage && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <p className="text-sm text-red-600 dark:text-red-400">{post.errorMessage}</p>
                </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
                {post.status !== 'published' && post.status !== 'cancelled' && (
                    <button
                        onClick={() => onEdit(post)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <Edit className="w-4 h-4" />
                        Edit
                    </button>
                )}

                {post.status === 'needs_media' && onUploadMedia && (
                    <button
                        onClick={() => onUploadMedia(post)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                    >
                        <Upload className="w-4 h-4" />
                        Upload Media
                    </button>
                )}

                {(post.status === 'scheduled' || post.status === 'ready') && (
                    <>
                        <button
                            onClick={() => onCancel(post)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            Cancel
                        </button>
                        <button
                            onClick={() => onPublishNow(post)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-lg hover:from-primary-700 hover:to-indigo-700 transition-all"
                        >
                            <Play className="w-4 h-4" />
                            Publish Now
                        </button>
                    </>
                )}

                {post.status === 'published' && (
                    <button
                        onClick={() => onViewAnalytics(post)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                    >
                        <BarChart3 className="w-4 h-4" />
                        View Analytics
                    </button>
                )}
            </div>
        </div>
    );
};

export default PostCard;
